import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  ChevronLeft, FileCheck, FileText, ShoppingCart, Package, Award, Users,
  CheckCircle2, Circle, Clock, IndianRupee, Download, FolderOpen, Star, Gavel
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const h = React.createElement;

const statusBadge = (status) => {
  const cfg = {
    PENDING: "bg-amber-50 text-amber-700 border-amber-200",
    APPROVED: "bg-emerald-50 text-emerald-700 border-emerald-200",
    REJECTED: "bg-rose-50 text-rose-700 border-rose-200",
    RETURNED: "bg-orange-50 text-orange-700 border-orange-200",
    DRAFT: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return h("span", { className: `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${cfg[status] || cfg.DRAFT}` }, status);
};

const fmtINR = (n) => "\u20B9" + (n || 0).toLocaleString("en-IN");
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "\u2014";

function SanctionProposalDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [proposal, setProposal] = useState(null);
  const [full, setFull] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = await axios.get(`${API_URL}/sanction-proposals/${id}`, getAuthHeader());
      setProposal(p.data);
      const f = await axios.get(`${API_URL}/tender-prs/${p.data.requisition_id}/full`, getAuthHeader());
      setFull(f.data);
    } catch {
      toast.error("Failed to load sanction proposal");
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const downloadDoc = async (doc) => {
    try {
      const r = await axios.get(`${API_URL}/documents/${doc.id}/download`, { ...getAuthHeader(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = doc.original_filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  if (loading) return h("div", { className: "p-12 text-center text-slate-400" }, "Loading sanction proposal...");
  if (!proposal) return h("div", { className: "p-12 text-center text-slate-400" }, "Proposal not found");

  const req = full?.requisition || {};
  const td = full?.tender_data || {};
  const pos = full?.purchase_orders || [];
  const docs = full?.documents || [];
  const history = full?.approval_history || [];
  const tInfo = td.tender_info || {};
  const l1 = td.l1_vendor || {};
  const shortlistedNames = td.shortlisted_vendors || [];
  const participants = td.participants || [];
  const shortlistedDetails = participants.filter(p => shortlistedNames.includes(p.name));

  // Milestones — compute completion
  const milestones = [
    { key: "pr_created", label: "PR Submitted", icon: FileText, when: req.created_at, done: !!req.created_at,
      detail: req.requisition_number },
    { key: "pr_approved", label: "PR Approved", icon: CheckCircle2, when: (req.approval_history || []).find(a => a.action === "APPROVE" && a.level === "DIRECTOR")?.timestamp,
      done: req.status === "APPROVED" || ["PROCURING", "AWARDED"].includes(req.status) },
    { key: "tender_notice", label: "Tender Notice (NIT)", icon: Gavel, when: tInfo.nit_date, done: !!tInfo.tender_number,
      detail: tInfo.tender_number ? `${tInfo.tender_number} on ${tInfo.portal || ""}` : "" },
    { key: "bid_opening", label: "Bid Opening", icon: Users, when: tInfo.financial_bid_opening || tInfo.tech_bid_opening, done: participants.length > 0,
      detail: participants.length > 0 ? `${participants.length} bidder${participants.length > 1 ? "s" : ""}` : "" },
    { key: "tech_eval", label: "Technical Evaluation", icon: Star, done: (td.technical_evaluation || []).length > 0,
      detail: participants.filter(p => p.qualified).length + " qualified" },
    { key: "l1", label: "L1 Identified", icon: Award, when: td.updated_at, done: !!l1.name,
      detail: l1.name ? `${l1.name} \u2014 ${fmtINR(l1.bid_amount)}` : "" },
    { key: "sanction", label: "Sanction Proposal", icon: FileCheck, when: proposal.created_at, done: true,
      detail: `${proposal.proposal_number} \u2014 ${proposal.status}` },
    { key: "po", label: "Purchase Order Created", icon: ShoppingCart, when: pos[0]?.created_at, done: pos.length > 0,
      detail: pos.length > 0 ? `${pos.length} PO${pos.length > 1 ? "s" : ""} \u2014 ${fmtINR(pos.reduce((s, p) => s + (p.po_value || p.total_amount || 0), 0))}` : "" },
    { key: "grn", label: "Goods Received", icon: Package, done: pos.some(p => p.grn_received), detail: "" },
  ];

  // Header
  return h("div", { className: "max-w-6xl mx-auto space-y-6", "data-testid": "sanction-proposal-detail" },
    h("div", { className: "flex items-center gap-4" },
      h(Button, { variant: "outline", size: "sm", onClick: () => navigate("/sanction-proposals"), "data-testid": "back-btn" },
        h(ChevronLeft, { className: "w-4 h-4 mr-1" }), "Back"
      ),
      h("div", { className: "flex-1" },
        h("div", { className: "flex items-center gap-3 flex-wrap" },
          h("h1", { className: "text-xl font-bold text-slate-900 font-mono" }, proposal.proposal_number),
          statusBadge(proposal.status),
          h(Badge, { variant: "outline", className: "text-xs" }, proposal.purpose)
        ),
        h("p", { className: "text-sm text-slate-500 mt-1" },
          "Requisition: ", h("span", { className: "font-semibold text-slate-700" }, proposal.requisition_number),
          " \u2022 Indenter: ", h("span", { className: "text-slate-700" }, proposal.indenter_name),
          " \u2022 ", h("span", { className: "font-bold text-emerald-700 ml-1" }, fmtINR(proposal.final_price))
        )
      )
    ),

    // Milestone Timeline
    h(Card, { className: "border-slate-200" },
      h(CardHeader, { className: "pb-3 border-b border-slate-100" },
        h(CardTitle, { className: "text-sm font-semibold uppercase tracking-wider text-slate-700 flex items-center gap-2" },
          h(Clock, { className: "w-4 h-4" }), "Procurement Milestone Timeline"
        )
      ),
      h(CardContent, { className: "p-6" },
        h("ol", { className: "relative border-l-2 border-slate-200 ml-3 space-y-6" },
          milestones.map((m, idx) => {
            const Icon = m.icon;
            return h("li", { key: m.key, className: "ml-6 relative", "data-testid": `milestone-${m.key}` },
              h("span", {
                className: "absolute -left-[34px] flex items-center justify-center w-7 h-7 rounded-full ring-4 ring-white " +
                  (m.done ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-400")
              }, m.done ? h(Icon, { className: "w-3.5 h-3.5" }) : h(Circle, { className: "w-3 h-3" })),
              h("div", { className: "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1" },
                h("div", null,
                  h("h4", { className: "text-sm font-semibold " + (m.done ? "text-slate-900" : "text-slate-400") }, m.label),
                  m.detail && h("p", { className: "text-xs text-slate-500 mt-0.5" }, m.detail)
                ),
                m.when && h("p", { className: "text-xs text-slate-400 font-mono" }, fmtDate(m.when))
              )
            );
          })
        )
      )
    ),

    // Two-column: Tender Info + Shortlisted Vendors
    h("div", { className: "grid grid-cols-1 lg:grid-cols-2 gap-6" },
      h(Card, { className: "border-slate-200" },
        h(CardHeader, { className: "pb-3 border-b border-slate-100" },
          h(CardTitle, { className: "text-sm font-semibold uppercase tracking-wider text-slate-700 flex items-center gap-2" },
            h(Gavel, { className: "w-4 h-4" }), "Tender Details"
          )
        ),
        h(CardContent, { className: "p-5 space-y-2 text-sm" },
          h("div", { className: "flex justify-between" }, h("span", { className: "text-slate-500" }, "Tender No."), h("span", { className: "font-medium text-slate-900 font-mono" }, tInfo.tender_number || "\u2014")),
          h("div", { className: "flex justify-between" }, h("span", { className: "text-slate-500" }, "Portal"), h("span", { className: "font-medium" }, tInfo.portal || "\u2014")),
          h("div", { className: "flex justify-between" }, h("span", { className: "text-slate-500" }, "NIT Date"), h("span", { className: "font-medium" }, fmtDate(tInfo.nit_date))),
          h("div", { className: "flex justify-between" }, h("span", { className: "text-slate-500" }, "Tender Type"), h("span", { className: "font-medium" }, (tInfo.tender_type || "\u2014").replace(/_/g, " "))),
          h("div", { className: "flex justify-between" }, h("span", { className: "text-slate-500" }, "Bid End"), h("span", { className: "font-medium" }, fmtDate(tInfo.bid_submission_end))),
          h("div", { className: "flex justify-between border-t pt-2 mt-2" }, h("span", { className: "text-slate-500" }, "L1 Vendor"), h("span", { className: "font-bold text-emerald-700" }, l1.name || "\u2014")),
          h("div", { className: "flex justify-between" }, h("span", { className: "text-slate-500" }, "L1 Value"), h("span", { className: "font-bold text-emerald-700" }, l1.bid_amount ? fmtINR(parseFloat(l1.bid_amount)) : "\u2014"))
        )
      ),
      h(Card, { className: "border-slate-200" },
        h(CardHeader, { className: "pb-3 border-b border-slate-100" },
          h(CardTitle, { className: "text-sm font-semibold uppercase tracking-wider text-slate-700 flex items-center gap-2" },
            h(Award, { className: "w-4 h-4" }), "Shortlisted Vendors (", shortlistedDetails.length, ")"
          )
        ),
        h(CardContent, { className: "p-5" },
          shortlistedDetails.length > 0 ? h("div", { className: "space-y-2" },
            shortlistedDetails.map((v, i) => h("div", { key: i, className: "flex items-center justify-between p-3 rounded-lg border " + (v.name === l1.name ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200") },
              h("div", null,
                h("p", { className: "text-sm font-semibold text-slate-900" }, v.name),
                h("p", { className: "text-xs text-slate-500" }, v.gst ? "GST: " + v.gst : (v.address || ""))
              ),
              h("div", { className: "text-right" },
                v.name === l1.name && h(Badge, { className: "bg-emerald-500 text-white text-xs mb-1" }, "L1"),
                h("p", { className: "text-sm font-bold text-slate-900" }, fmtINR(parseFloat(v.bid_amount) || 0))
              )
            ))
          ) : h("p", { className: "text-sm text-slate-400 text-center py-4" }, "No vendors shortlisted yet")
        )
      )
    ),

    // Purchase Orders
    pos.length > 0 && h(Card, { className: "border-slate-200" },
      h(CardHeader, { className: "pb-3 border-b border-slate-100" },
        h(CardTitle, { className: "text-sm font-semibold uppercase tracking-wider text-slate-700 flex items-center gap-2" },
          h(ShoppingCart, { className: "w-4 h-4" }), "Purchase Orders (", pos.length, ")"
        )
      ),
      h(CardContent, { className: "p-0" },
        h(Table, null,
          h(TableHeader, null, h(TableRow, { className: "bg-slate-50/80" },
            h(TableHead, { className: "font-semibold" }, "PO Number"),
            h(TableHead, { className: "font-semibold" }, "Date"),
            h(TableHead, { className: "font-semibold" }, "Vendor"),
            h(TableHead, { className: "font-semibold" }, "Type"),
            h(TableHead, { className: "font-semibold text-right" }, "Value"),
            h(TableHead, { className: "font-semibold" }, "Status"),
            h(TableHead, { className: "w-10" })
          )),
          h(TableBody, null, pos.map(p => h(TableRow, { key: p.id, className: "cursor-pointer hover:bg-slate-50/50", onClick: () => navigate(`/purchase-orders/${p.id}`) },
            h(TableCell, { className: "font-mono text-sm font-semibold" }, p.po_number),
            h(TableCell, { className: "text-xs text-slate-600" }, fmtDate(p.po_date || p.created_at)),
            h(TableCell, { className: "text-sm" }, p.vendor_details?.name || "\u2014"),
            h(TableCell, null, h(Badge, { variant: "outline", className: "text-xs" }, (p.po_type || "DOMESTIC") + " / " + (p.po_sub_type || "GENERAL"))),
            h(TableCell, { className: "text-right font-semibold" }, fmtINR(p.po_value || p.total_amount || 0)),
            h(TableCell, null, statusBadge(p.status || "DRAFT")),
            h(TableCell, null, h(FileText, { className: "w-4 h-4 text-slate-400" }))
          )))
        )
      )
    ),

    // Documents
    h(Card, { className: "border-slate-200" },
      h(CardHeader, { className: "pb-3 border-b border-slate-100" },
        h(CardTitle, { className: "text-sm font-semibold uppercase tracking-wider text-slate-700 flex items-center gap-2" },
          h(FolderOpen, { className: "w-4 h-4" }), "Documents (", docs.length, ")"
        )
      ),
      h(CardContent, { className: "p-0" },
        docs.length > 0 ? h(Table, null,
          h(TableHeader, null, h(TableRow, { className: "bg-slate-50/80" },
            h(TableHead, { className: "font-semibold" }, "Type"),
            h(TableHead, { className: "font-semibold" }, "File"),
            h(TableHead, { className: "font-semibold" }, "Uploaded"),
            h(TableHead, { className: "w-12" })
          )),
          h(TableBody, null, docs.map(d => h(TableRow, { key: d.id, className: "hover:bg-slate-50/50" },
            h(TableCell, null, h(Badge, { variant: "outline", className: "text-xs" }, d.document_type)),
            h(TableCell, { className: "text-sm" }, d.original_filename),
            h(TableCell, { className: "text-xs text-slate-500" }, d.uploaded_by_name + " \u2022 " + fmtDate(d.created_at)),
            h(TableCell, null, h(Button, { variant: "ghost", size: "icon", className: "h-7 w-7", onClick: () => downloadDoc(d), "data-testid": "download-doc-" + d.id }, h(Download, { className: "w-3.5 h-3.5 text-blue-600" })))
          )))
        ) : h("div", { className: "p-8 text-center text-sm text-slate-400" }, "No documents uploaded")
      )
    ),

    // Approval History
    history.length > 0 && h(Card, { className: "border-slate-200" },
      h(CardHeader, { className: "pb-3 border-b border-slate-100" },
        h(CardTitle, { className: "text-sm font-semibold uppercase tracking-wider text-slate-700 flex items-center gap-2" },
          h(IndianRupee, { className: "w-4 h-4" }), "Requisition Approval History"
        )
      ),
      h(CardContent, { className: "p-5" },
        h("div", { className: "space-y-3" },
          history.map((a, i) => h("div", { key: i, className: "flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100" },
            h("div", { className: "w-2 h-2 rounded-full mt-2 " + (a.action === "APPROVE" ? "bg-emerald-500" : a.action === "REJECT" ? "bg-rose-500" : "bg-amber-500") }),
            h("div", { className: "flex-1" },
              h("div", { className: "flex items-center justify-between" },
                h("p", { className: "text-sm font-semibold text-slate-900" }, a.user_name, h("span", { className: "text-xs text-slate-500 ml-2" }, "(", a.level, ")")),
                h("p", { className: "text-xs text-slate-500 font-mono" }, fmtDate(a.timestamp))
              ),
              h("p", { className: "text-xs text-slate-600 mt-0.5" }, a.action, a.remarks ? " \u2014 " + a.remarks : "")
            )
          ))
        )
      )
    )
  );
}

export default SanctionProposalDetail;
